import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, Library } from "lucide-react";
import { toast } from "sonner";
import { getToken } from "@/lib/api";
import { MediaLibrary } from "@/components/admin/MediaLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC = process.env.REACT_APP_BACKEND_URL;

export const ImageField = ({ label, value, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large. Max 8 MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/upload`, form, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
      });
      onChange(`${PUBLIC}${data.url}`);
      toast.success("Image uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-1.5">
      <label className="text-[10px] uppercase tracking-[0.25em] text-ivory/55">{label}</label>
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a URL, upload, or pick from library"
          className="flex-1 min-w-[180px] bg-midnight/70 border border-gold/20 px-3 py-2 text-ivory text-sm focus:outline-none focus:border-gold"
        />
        <button
          type="button"
          onClick={() => setLibOpen(true)}
          data-testid="image-library-btn"
          className="btn-ghost px-3 py-2 text-[10px]"
        >
          <Library className="h-4 w-4" /> Library
        </button>
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid="image-upload-btn"
          className="btn-ghost px-3 py-2 disabled:opacity-50 text-[10px]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={upload}
          className="hidden"
        />
      </div>
      {value && (
        <img src={value} alt="preview" className="mt-2 h-24 w-auto object-cover border border-gold/20" />
      )}
      <MediaLibrary open={libOpen} onClose={() => setLibOpen(false)} onSelect={onChange} />
    </div>
  );
};

export default ImageField;
